import React, { useEffect, useRef, useState } from 'react'
import Content from '../../../blocks.app/content/content'
import translate from '../../../core/translate'
import { Email, IEmail } from './__email/Email'
import styles from './notificationEmails.jcss'
import Checkbox from '../../../blocks.simple/checkbox/checkbox'
import Icon from '../../../blocks.simple/icon/icon'
import moment from 'moment'
import { api } from '../../../core/api/ConnectionManager'
import { cn } from 'ethcss'
import helpers from 'core/helpers'
import InfiniteScroll from 'react-infinite-scroller'
import LoaderLazy from 'blocks.app/loader/_lazy/loader__lazy'
import { EventDetails, IEventDetailsField } from 'organisms/EventDetails'
import { Button } from 'molecules/Button'

const EMAILS_PAGE_SIZE = 30
const MAX_EMAILS_IN_MEMORY = 1000

const mergeEmails = (current: IEmail[], incoming: IEmail[], prepend = false) => {
    const seen = new Set<number>()
    const source = prepend ? [...incoming, ...current] : [...current, ...incoming]

    return source
        .filter((email) => {
            if (seen.has(email.id)) return false

            seen.add(email.id)
            return true
        })
        .slice(0, MAX_EMAILS_IN_MEMORY)
}

export const NotificationEmails = (p_: {
    getKeepDurationSettings: (field: string, defaultValue: string) => string
    goToSettings: () => void
}) => {
    const [emails, setEmails] = useState<IEmail[]>([])
    const [selectedEmails, setSelectedEmails] = useState<number[]>([])
    const [confirmDeleteEmail, setConfirmDeleteEmail] = useState<number[] | null>(null)
    const [modalInfo, setModalInfo] = useState<IEmail | null>(null)
    const [token, setToken] = useState<string>('')
    const emailsRef = useRef<IEmail[]>([])
    const tokenRef = useRef('')
    const isMountedRef = useRef(true)
    const isLoadingRef = useRef(false)
    const requestIdRef = useRef(0)

    const updateEmails = (next: IEmail[]) => {
        emailsRef.current = next
        setEmails(next)
    }

    const updateToken = (nextToken = '') => {
        tokenRef.current = nextToken
        setToken(nextToken)
    }

    const getEmailStatus = (status: string) => {
        switch (status) {
            case 'sent':
                return translate('mailSent')
            case 'in_delivery':
                return translate('mailInDelivery')
            case 'delivered':
                return translate('mailDelivered')
            case 'error':
                return translate('mailError')
            default:
                return ''
        }
    }
    const onSelect = (id: number, selected: boolean) => {
        setSelectedEmails((current) =>
            selected ? Array.from(new Set([...current, id])) : current.filter((email) => email !== id)
        )
    }
    const onSelectAll = (selected: boolean) => {
        if (selected) {
            setSelectedEmails(emails.map((email) => email.id))
        } else {
            setSelectedEmails([])
        }
    }
    const onCloseConfirm = () => {
        setConfirmDeleteEmail(null)
    }
    const onDeleteAll = () => {
        setConfirmDeleteEmail(selectedEmails)
    }
    const onConfirmDelete = () => {
        if (!confirmDeleteEmail) return

        api.send('deleteEmailNotifications', { id: confirmDeleteEmail }).then(() => {
            if (confirmDeleteEmail.length === emails.length) {
                getEmailNotifications(true, true)
            }

            setSelectedEmails([])
            onCloseConfirm()
        })
    }
    const onClose = () => {
        setModalInfo(null)
    }
    const onDetails = (email: IEmail) => {
        setModalInfo(email)
    }

    const getEmailStatusColor = (status: string) => {
        switch (status) {
            case 'error':
                return '#EB5757'
            case 'delivered':
                return '#27AE60'
            case 'in_delivery':
                return '#F2C94C'
            default:
                return '#2D9CDB'
        }
    }

    const getEmailDetails = (email: IEmail): IEventDetailsField[][] => [
        [
            {
                label: translate('lastChangeDate'),
                value: email.statusUpdatedAt ? moment(email.statusUpdatedAt).format('YYYY.MM.DD - HH:mm') : '',
            },
            {
                label: translate('status'),
                value: getEmailStatus(email.status),
                accentColor: getEmailStatusColor(email.status),
            },
        ],
        [
            {
                label: translate('mailFrom'),
                value: email.from,
            },
            {
                label: translate('mailTo'),
                value: email.to,
            },
            {
                label: translate('mailSubject'),
                value: email.message.subject,
            },
        ],
        ...(email.error
            ? [
                  [
                      {
                          label: translate('errorText'),
                          value: email.error,
                      },
                  ],
              ]
            : []),
        [
            {
                label: translate('message'),
                value: (
                    <div
                        className={styles.emailBody}
                        dangerouslySetInnerHTML={{
                            __html: email.message.html,
                        }}
                    />
                ),
            },
        ],
    ]

    useEffect(() => {
        getEmailNotifications(true, true)

        return () => {
            isMountedRef.current = false
            requestIdRef.current += 1
        }
    }, [])

    useEffect(() => {
        const listenersId: string[] = []

        api.addObserver(
            'emailNotificationCreated',
            (email: any) => {
                if (!email) return

                const incoming = Array.isArray(email) ? email : [email]
                updateEmails(mergeEmails(emailsRef.current, incoming, true))
            },
            listenersId
        )
        api.addObserver(
            'emailNotificationUpdated',
            (email: any) => {
                if (!email) return

                updateEmails(
                    emailsRef.current.map((item) => {
                        if (item.id === email.id) {
                            return email
                        }

                        return item
                    })
                )
            },
            listenersId
        )
        api.addObserver(
            'emailNotificationDeleted',
            (ids: any) => {
                if (!ids) return

                const deletedIds = Array.isArray(ids) ? ids : [ids]
                updateEmails(emailsRef.current.filter((email) => !deletedIds.includes(email.id)))
                setSelectedEmails((current) => current.filter((id) => !deletedIds.includes(id)))
            },
            listenersId
        )

        return () => {
            listenersId.forEach((id: any) => api.removeObserver(id))
        }
    }, [])

    const getEmailNotifications = (isInitial: boolean, hasLimit: boolean) => {
        if (!isInitial && (isLoadingRef.current || !tokenRef.current)) return

        const requestId = requestIdRef.current + 1
        const requestedToken = isInitial ? '' : tokenRef.current
        const params: {
            sort: string[]
            nextPageToken?: string
            limit?: number
        } = isInitial
            ? {
                  sort: ['statusUpdatedAt', 'DESC'],
              }
            : {
                  sort: ['statusUpdatedAt', 'DESC'],
                  nextPageToken: requestedToken,
              }

        if (hasLimit) {
            params.limit = EMAILS_PAGE_SIZE
        }

        requestIdRef.current = requestId
        isLoadingRef.current = true

        api.send('getEmailNotifications', params)
            .then(
                (response: any) => {
                    if (!isMountedRef.current || requestId !== requestIdRef.current || !response?.data) return

                    const next = isInitial
                        ? mergeEmails([], response.data)
                        : mergeEmails(emailsRef.current, response.data)
                    const didAppend = next.length > emailsRef.current.length
                    const reachedLimit = next.length >= MAX_EMAILS_IN_MEMORY

                    updateEmails(next)
                    updateToken(
                        !isInitial && (!didAppend || reachedLimit || response.nextPageToken === requestedToken)
                            ? ''
                            : response.nextPageToken
                    )
                },
                () => undefined
            )
            .then(() => {
                if (requestId === requestIdRef.current) isLoadingRef.current = false
            })
    }

    const getAllEmailNotifications = () => {
        getEmailNotifications(true, false)
    }

    return (
        <Content wrapperClassName={cn(styles.emailsWrapper)} title="eventJournal">
            <div className={styles.caption}>
                {translate('messageKeepDuration')}: {p_.getKeepDurationSettings('emailHistoryLifespanD', '30')}
                <div className={styles.changeSettings} onClick={p_.goToSettings}>
                    {translate('editLabel')}
                </div>
            </div>
            <div className={styles.header}>
                <div className={styles.group}>
                    <Button mod={'fillBrand'} onClick={() => getAllEmailNotifications()}>
                        {translate('getAll')}
                    </Button>
                </div>
            </div>
            <div className={styles.table}>
                <div className={styles.tableHeader} role="row">
                    <div className={styles.checkboxCell} role="columnheader">
                        <Checkbox
                            onClick={(selected: boolean) => onSelectAll(selected)}
                            checked={!!selectedEmails.length && selectedEmails.length === emails.length}
                        />
                    </div>
                    <div role="columnheader">{translate('session')}</div>
                    <div role="columnheader">{translate('status')}</div>
                    <div role="columnheader">{translate('mailFrom')}</div>
                    <div role="columnheader">{translate('mailSubject')}</div>
                    <div className={styles.rowAction} role="columnheader">
                        {helpers.isAvailable({ key: 'emailHistory', action: 'delete' }) && (
                            <Icon
                                type={'delete'}
                                size={17}
                                tooltip={{ title: translate('delete') }}
                                disabled={!selectedEmails.length}
                                onClick={onDeleteAll}
                            />
                        )}
                    </div>
                </div>
                <InfiniteScroll
                    loadMore={() => getEmailNotifications(false, true)}
                    hasMore={!!token}
                    initialLoad={false}
                    loader={<LoaderLazy key="history__loader" active={true} />}
                    threshold={10}
                    useWindow={true}
                >
                    {emails.map((email) => (
                        <Email
                            key={email.id}
                            email={email}
                            selected={selectedEmails.includes(email.id)}
                            onSelect={onSelect}
                            onDetails={onDetails}
                            getStatus={getEmailStatus}
                        />
                    ))}
                </InfiniteScroll>
                {!emails.length && <div className={styles.emptyState}>{translate('noNewEvents')}</div>}
            </div>
            {confirmDeleteEmail && (
                <div className={styles.deleteConfirm}>
                    <div className={styles.deleteConfirmInner}>
                        <div className={styles.deleteConfirmMessage}>
                            {confirmDeleteEmail.length > 1
                                ? translate('mailsNotificationDeleteConfirm')
                                : translate('mailNotificationDeleteConfirm')}
                        </div>
                        <div className={styles.deleteConfirmButtons}>
                            <Button className={styles.button} onClick={onCloseConfirm}>
                                {translate('cancel')}
                            </Button>
                            <Button mod={'fillBlack'} className={styles.button} onClick={onConfirmDelete}>
                                {translate('delete')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {modalInfo && (
                <EventDetails
                    title={translate('eventDetails')}
                    backLabel={translate('back')}
                    groups={getEmailDetails(modalInfo)}
                    onBack={onClose}
                />
            )}
        </Content>
    )
}
